/**
 * Liveness: noticing that the world changed, and that somebody else is in it.
 *
 * Michael, 2026-09-17 (#1) "All changes in WLX need to save and update across all
 * devices", (#2) "Updates in WLX or NetSuite need to reflect everywhere in WLX
 * including flags", and 2026-09-19 "some type of indicator that someone else is
 * actively working on a time entry ... I don't want two people working on the same
 * thing."
 *
 * 🔥 THESE ARE ONE PROBLEM. WorkLogix had NO refresh of any kind — every page loaded
 * once and was stale from that instant. Two supervisors never saw each other's
 * approvals; a NetSuite earning could be added and the flag stayed up until somebody
 * reloaded the browser. Presence needs exactly the same heartbeat, so all three are
 * served by one poll rather than three mechanisms drifting apart.
 *
 * WHY POLLING AND NOT A WEBSOCKET. SalesLogix does this with SignalR; WorkLogix has no
 * socket layer at all, and adding one is a large piece of infrastructure to answer two
 * small, tolerant questions — "has anything changed?" and "is anyone else here?".
 * Neither needs sub-second latency, both are read-only, and a poll degrades gracefully
 * when the network blips where a dropped socket needs reconnection logic to do the
 * same. The cost is one cheap request per open tab per interval, which is why the
 * watermark exists: the poll is small, and only a CHANGE pays for a full refetch.
 */
/** How often an open page polls. */
export const LIVENESS_POLL_MS = 15_000;
/**
 * How long a presence heartbeat is believed after it was last written.
 *
 * 🔥 GENEROUSLY MORE THAN THE POLL INTERVAL, and that ratio is the whole design. A
 * browser that is closed, crashes, sleeps or is killed cannot be relied on to clean up
 * after itself, so presence must EXPIRE rather than be deleted. Too tight and an
 * indicator flickers off between a slow tab's heartbeats, which reads as "they left"
 * while somebody is still typing; too loose and a closed laptop warns people off an
 * entry for minutes. Three missed polls is the tolerance.
 */
export const PRESENCE_STALE_MS = 45_000;
/**
 * Has anything changed since the watermark we hold?
 *
 * Returns true when we hold nothing yet — the first poll after a load has no basis for
 * comparison, and claiming "unchanged" would suppress the first legitimate refresh.
 */
export function watermarkChanged(held, fresh) {
    if (held === null) {
        return true;
    }
    return held.latestChange !== fresh.latestChange || held.count !== fresh.count;
}
/**
 * Is this heartbeat still worth believing?
 *
 * Takes both instants so it is a pure function of its arguments — a presence rule that
 * read the clock could not be tested for the boundary, which is the only part of it
 * anybody will ever get wrong.
 */
export function isPresenceFresh(lastSeenAt, now) {
    const seen = Date.parse(lastSeenAt);
    const at = Date.parse(now);
    if (Number.isNaN(seen) || Number.isNaN(at)) {
        // An unparseable stamp must not manufacture a warning about a person who may not
        // be there. Fail toward saying nothing.
        return false;
    }
    const age = at - seen;
    // A heartbeat from the FUTURE is a clock-skew artefact between two machines, not
    // evidence of absence — treat it as present rather than flapping the indicator.
    return age <= PRESENCE_STALE_MS;
}
/**
 * How to describe the people on an entry, in the one line the badge has room for.
 *
 * Names, never counts alone: "Edwin Martinez is editing this" tells you who to go and
 * ask, which is the entire point of the indicator. "1 other person" does not.
 */
export function describePresence(names) {
    const unique = [...new Set(names.filter((n) => n.trim() !== ''))];
    if (unique.length === 0) {
        return '';
    }
    if (unique.length === 1) {
        return `${unique[0] ?? ''} is working on this entry`;
    }
    if (unique.length === 2) {
        return `${unique[0] ?? ''} and ${unique[1] ?? ''} are working on this entry`;
    }
    return `${unique[0] ?? ''} and ${String(unique.length - 1)} others are working on this entry`;
}
//# sourceMappingURL=liveness.js.map